/**
 * LearningCard - Card agnóstica para contenido de aprendizaje
 * Variantes: resource | story (con estilos normal | featured | wide)
 */

import { useTranslation } from 'react-i18next';
import { CircleStar, Check } from 'lucide-react';
import { cn } from '@/utils/cn';
import { formatXP } from '@/utils/formatXP';
import { Icon } from '@/components/atoms/Icon';
import { Button } from '@/components/atoms/Button';
import styles from './LearningCard.module.scss';
import type { LearningCardProps } from './LearningCard.types';

export function LearningCard({
  use,
  style = 'normal',
  compact = false,
  title,
  description,
  tag,
  level,
  completed = false,
  xpReward,
  backgroundImage,
  onClick,
  id,
  className,
  showButton = false,
  buttonText,
  buttonVariant = 'primary',
  onButtonClick,
  buttonDisabled = false,
}: LearningCardProps) {
  const { t } = useTranslation();
  const resolvedButtonText = buttonText ?? t('learningCard.defaultStart');
  const isResource = use === 'resource';
  const hasImage = Boolean(backgroundImage);
  const compactWithImage = compact && hasImage && !isResource;

  const cardClasses = cn(
    styles.learningCard,
    styles[`learningCard--${use}`],
    styles[`learningCard--${use}-${style}`],
    compact && !compactWithImage && styles['learningCard--compact'],
    onClick && styles['learningCard--clickable'],
    completed && styles['learningCard--completed'],
    !compactWithImage && hasImage && styles['learningCard--hasImage'],
    compactWithImage && styles['learningCard--compactImage'],
    className
  );

  const cardStyle = !compactWithImage && hasImage
    ? { backgroundImage: `url(${backgroundImage})` }
    : undefined;

  return (
    // TODO accesibilidad: convertir a <button> o añadir role/keyboard. Pre-existente.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div id={id} className={cardClasses} style={cardStyle} onClick={onClick}>
      {compactWithImage && (
        <div
          className={styles.learningCard__thumbnail}
          style={{ backgroundImage: `url(${backgroundImage})` }}
          aria-hidden="true"
        >
          {completed && (
            <>
              <div className={styles.learningCard__thumbnailOverlay} aria-hidden="true" />
              <div className={styles.learningCard__thumbnailCheck}>
                <Icon icon={Check} size="sm" />
              </div>
            </>
          )}
        </div>
      )}

      {!compact && (
        <div className={styles.learningCard__header}>
          <div className={styles.learningCard__tag}>
            <span className={styles.learningCard__tagText}>{tag.toUpperCase()}</span>
          </div>
          {isResource && level && (
            <div className={styles.learningCard__levelBadge}>
              <span className={styles.learningCard__levelText}>{level}</span>
            </div>
          )}
          {completed && (
            <div className={styles.learningCard__completedBadge} aria-label={t('learningCard.completedAria')}>
              <Icon icon={Check} size="xs" />
            </div>
          )}
        </div>
      )}

      <div className={styles.learningCard__body}>
        <h3 className={styles.learningCard__title}>{title}</h3>
        <p className={styles.learningCard__description}>{description}</p>
      </div>

      {!compact && (
        <div className={styles.learningCard__footer}>
          <div className={styles.learningCard__meta}>
            <Icon icon={CircleStar} size="sm" />
            <span>{formatXP(xpReward)} XP</span>
          </div>
        </div>
      )}

      {!compact && !isResource && showButton && (
        <div className={styles.learningCard__buttonWrapper}>
          <Button
            variant={buttonVariant}
            size="md"
            fullWidth
            disabled={buttonDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onButtonClick?.(e);
            }}
          >
            {resolvedButtonText}
          </Button>
        </div>
      )}
    </div>
  );
}

LearningCard.displayName = 'LearningCard';
